// UserMessage.tsx
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, CircularProgress, Paper, Stack } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { komplyanceAiChatMessage } from '@app/pages';
import { useTrans } from '@libs/i18n';
import { detectDirection } from '@libs/utils';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { getChatDocumentDownloadUrl } from '../../api/documents';
import { MAX_VISIBLE_CHIPS } from '../../constants';
import { resolveDocumentConversationId } from '../../utils/draftConversation';
import { extractFileExtension } from '../../utils/fileUtils';
import { FileChip, FileChipAction, FileChipOverflow, FileChipRow } from '../FileChip';

interface UserBubbleProps {
  message: komplyanceAiChatMessage;
  conversationId?: string;
}

export const UserBubble: React.FC<UserBubbleProps> = observer(({ message, conversationId }) => {
  const { t } = useTrans();
  const hasContent = !!message.content;
  const hasFiles = !!message.files?.length;
  const [downloadingIdx, setDownloadingIdx] = useState<number | null>(null);
  const [downloadErrorIdx, setDownloadErrorIdx] = useState<number | null>(null);

  if (!hasContent && !hasFiles) return null;

  const dynamiDirection = detectDirection(message.content || '');

  const handleDownload = async (file: NonNullable<komplyanceAiChatMessage['files']>[number], idx: number) => {
    if (!file.docId || !conversationId) return;

    setDownloadErrorIdx(null);
    try {
      setDownloadingIdx(idx);
      const fileExtension = extractFileExtension(file.fileName);
      // A document attached on the new-chat screen was filed under a draft
      // conversation, so the download key comes from its stored s3Key rather than
      // the conversation we are now viewing.
      const { s3DownloadUrl } = await getChatDocumentDownloadUrl(
        file.docId,
        resolveDocumentConversationId(file.s3Key, conversationId),
        fileExtension,
      );

      // Fetch as blob so the download attribute works across origins (no new tab)
      const blob = await fetch(s3DownloadUrl).then((r) => r.blob());
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('[UserBubble] Download failed:', error);
      setDownloadErrorIdx(idx);
    } finally {
      setDownloadingIdx(null);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        pt: 2,
        pb: 2.5,
        '@keyframes userBubbleIn': {
          from: { opacity: 0, transform: 'translateY(12px) scale(.985)' },
          to: { opacity: 1, transform: 'none' },
        },
        animation: 'userBubbleIn .34s cubic-bezier(.22,.8,.3,1) both',
      }}
    >
      {/* Capped in absolute terms too — the thread column is wide, and a question
          stretched across all of it stops reading as a question. */}
      <Stack spacing={1} sx={{ maxWidth: 'min(82%, 720px)', alignItems: 'flex-end' }}>
        {/* Sent attachments — the same chips the composer staged, now on grey and
            carrying a download control instead of a remove one. */}
        {hasFiles && (
          <FileChipRow align="end">
            {(message.files ?? []).slice(0, MAX_VISIBLE_CHIPS).map((file, idx) => {
              const isDownloadable = !!file.docId && !!conversationId;
              const isDownloading = downloadingIdx === idx;
              const hasDownloadError = downloadErrorIdx === idx;

              return (
                <FileChip
                  key={idx}
                  variant="sent"
                  fileName={file.fileName}
                  mimeType={file.mimeType}
                  hasError={hasDownloadError}
                  title={
                    hasDownloadError
                      ? (t('chat.attachment.downloadError') as string)
                      : file.fileName
                  }
                  action={
                    isDownloadable ? (
                      <FileChipAction
                        tone="neutral"
                        size={24}
                        disabled={isDownloading}
                        onClick={() => handleDownload(file, idx)}
                        ariaLabel={t('chat.attachment.download') as string}
                      >
                        {isDownloading ? (
                          <CircularProgress size={14} thickness={6} />
                        ) : (
                          <DownloadSimpleIcon size={15} />
                        )}
                      </FileChipAction>
                    ) : undefined
                  }
                />
              );
            })}

            {(message.files?.length ?? 0) > MAX_VISIBLE_CHIPS && (
              <FileChipOverflow count={(message.files?.length ?? 0) - MAX_VISIBLE_CHIPS} />
            )}
          </FileChipRow>
        )}

        {/* Message Content */}
        {hasContent && (
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 0.25,
              backgroundColor: '#E8F1FB',
              borderRadius: (theme) =>
                theme.direction === 'rtl' ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <NoFlipBox
              mode={dynamiDirection}
              sx={{
                fontSize: '0.844rem',
                fontWeight: 400,
                lineHeight: 1.6,
                color: 'text.primary',
                fontFamily: 'Readex Pro',
                unicodeBidi: 'plaintext',
                '& p': { my: 1.25 },
              }}
            >
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </NoFlipBox>
          </Paper>
        )}
      </Stack>
    </Box>
  );
});
